import React from "react";
import { Button, DatePicker, Input, Select, Space, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const ValidPassportsTable: React.FC<
  IAdditionalInfoTableProps<IValidPassportInfo>
> = ({ dataSource, handleChange, handleAdd, handleDelete }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<IValidPassportInfo> = [
    {
      title: t("number_short"),
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: t("apply_visa.visa_type"),
      dataIndex: "passport_type",
      key: "passport_type",
      width: 200,
      render: (_, record) => (
        <Select
          value={record.passport_type}
          className="min-w-[200px]"
          placeholder={t("registration_form.step3.visa_type_hint")}
          style={{ width: "100%" }}
          onChange={(value) => handleChange(record.key, "passport_type", value)}
          options={[
            {
              value: "ordinary",
              label: t("registration_form.step3.ordinary_passport")
            },
            {
              value: "diplomatic",
              label: t("registration_form.step3.diplomatic_passport")
            },
            {
              value: "official",
              label: t("registration_form.step3.official_passport")
            },
            {
              value: "other",
              label: t("registration_form.step3.other_passport")
            }
          ]}
        />
      )
    },
    {
      title: t("additional_table.valid_passports.specify.title"),
      dataIndex: "passport_specify",
      key: "passport_specify",
      render: (_, record) => (
        <Input
          className="min-w-[200px] w-full"
          placeholder={t("additional_table.valid_passports.specify.hint")}
          value={record.passport_specify}
          onChange={(e) =>
            handleChange(record.key, "passport_specify", e.target.value)
          }
        />
      )
    },
    {
      title: t("registration_form.step3.passport_number"),
      dataIndex: "passport_number",
      key: "passport_number",
      render: (_, record) => (
        <Input
          className="min-w-[200px] w-full"
          placeholder={t("registration_form.step3.passport_number_hint")}
          value={record.passport_number}
          onChange={(e) =>
            handleChange(record.key, "passport_number", e.target.value)
          }
        />
      )
    },
    {
      title: t("additional_table.valid_passports.issue_place.title"),
      dataIndex: "passport_place_of_issue",
      key: "passport_place_of_issue",
      render: (_, record) => (
        <Input
          className="min-w-[200px] w-full"
          placeholder={t("additional_table.valid_passports.issue_place.hint")}
          value={record.passport_place_of_issue}
          onChange={(e) =>
            handleChange(record.key, "passport_place_of_issue", e.target.value)
          }
        />
      )
    },
    {
      title: t("registration_form.step3.date_of_issue"),
      dataIndex: "passport_date_of_issue",
      key: "passport_date_of_issue",
      width: 200,
      render: (_, record) => (
        <DatePicker
          value={
            record.passport_date_of_issue
              ? dayjs(record.passport_date_of_issue)
              : null
          }
          className="min-w-[200px]"
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          onChange={(date) =>
            handleChange(
              record.key,
              "passport_date_of_issue",
              date ? date.format("DD/MM/YYYY") : ""
            )
          }
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: t("registration_form.step3.expiry_date"),
      dataIndex: "passport_expiry_date",
      key: "passport_expiry_date",
      width: 200,
      render: (_, record) => (
        <DatePicker
          value={
            record.passport_expiry_date
              ? dayjs(record.passport_expiry_date)
              : null
          }
          className="min-w-[200px]"
          format="DD/MM/YYYY"
          placeholder="DD/MM/YYYY"
          onChange={(date) =>
            handleChange(
              record.key,
              "passport_expiry_date",
              date ? date.format("DD/MM/YYYY") : ""
            )
          }
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: t("cta.add_delete"),
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {dataSource.length > 1 && (
            <Button
              type="text"
              danger
              icon={<Trash2 size={18} color="red" />}
              onClick={() => handleDelete(record.key)}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: true }}
        className="mb-4"
      />

      <div className="w-full flex justify-end items-center">
        <Button
          type="primary"
          icon={<PlusCircle size={18} />}
          onClick={handleAdd}
        >
          {t("cta.add_row")}
        </Button>
      </div>
    </>
  );
};

export default ValidPassportsTable;
